import { useCallback } from 'react';

export function useLessonClipboardHandlers(
  getValues: () => any,
  reset: (values: any) => void,
  copy: (payload: any) => Promise<void>,
  paste: () => Promise<any>,
  getServiceDurationMinutes: (serviceId?: number) => number,
  addMinutes: (time: string, minutes: number) => string,
  watch: (name: any) => any,
  setValue: (name: any, value: any, options?: any) => void,
  toast: { error: (m: string) => void }
) {
  const handleCopyForm = useCallback(async () => {
    try {
      const values = getValues();
      const payload = { ...(values as Record<string, unknown>) };
      delete (payload as any).instructor_id;
      delete (payload as any).date;
      (payload as any)._meta = { createdAt: Date.now(), version: '1' };
      await copy(payload);
    } catch {
      toast.error('Failed to copy form');
    }
  }, [getValues, copy, toast]);

  const handlePasteForm = useCallback(async () => {
    try {
      const stored = await paste();
      if (!stored) return;

      const currentValues = getValues();
      const currentDate = currentValues.date;
      const currentInstructorId = currentValues.instructor_id;
      const currentStart = currentValues.start;

      const nextValues: any = { ...stored };
      delete nextValues.date;
      delete nextValues.instructor_id;
      delete nextValues.start;

      reset({
        ...currentValues,
        ...nextValues,
        date: currentDate,
        instructor_id: currentInstructorId,
        start: currentStart
      });

      const startForCalc = currentStart ?? watch('start');
      const serviceIdForCalc = nextValues.lesson_code_id as number | undefined;
      if (startForCalc) {
        const minutes = getServiceDurationMinutes(serviceIdForCalc);
        setValue('end', addMinutes(startForCalc, minutes), {
          shouldValidate: true
        });
      }
    } catch {
      toast.error('Failed to paste form');
    }
  }, [
    paste,
    getValues,
    reset,
    watch,
    getServiceDurationMinutes,
    addMinutes,
    setValue,
    toast
  ]);

  return { handleCopyForm, handlePasteForm };
}

export function useLessonConfirmations(
  openConfirm: (
    title: string,
    message: string,
    onConfirm: () => Promise<void> | void
  ) => void,
  toast: (m: string) => void,
  _deleteLesson: (id: number) => Promise<any>,
  _updateProcess: (payload: { id: number }) => Promise<any>,
  _lessonSchedule: any[],
  _setLessonSchedule: (next: any[]) => void,
  _modalConfig: any,
  _onClose?: () => void
) {
  const confirmCopyForm = useCallback(
    (doCopy: () => Promise<void>) => {
      openConfirm(
        'Copy lesson',
        'Are you sure you want to copy lesson?',
        async () => {
          await doCopy();
        }
      );
    },
    [openConfirm]
  );

  const confirmPasteForm = useCallback(
    (doPaste: () => Promise<void>, hasClipboard: boolean) => {
      if (!hasClipboard) {
        toast('Nothing to paste');
        return;
      }
      openConfirm(
        'Paste lesson',
        'Are you sure you want to paste lesson?',
        async () => {
          await doPaste();
        }
      );
    },
    [openConfirm, toast]
  );

  return { confirmCopyForm, confirmPasteForm };
}
